import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link2, Users } from '@digital-twins/design-system/icons'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Switch } from '@/components/ui/switch'
import { knowledgeThreadsApi } from '@/lib/api'
import { cn } from '@/lib/utils'
import { ROUTES } from '@/routes/routes'

import { KnowledgeThreadView } from '@/lib/api'

interface ThreadShareControlProps {
  threadToken: string
  isShared: boolean
  /** The thread as the server returns it after a share change. */
  onThreadChange: (thread: KnowledgeThreadView) => void
}

/**
 * Owner-only Share control (thread privacy rev 3). One toggle opens the thread
 * view-only to the organization; teammates can read it and fork their own
 * copy. Copy link hands out the thread URL, which only opens for teammates
 * while the thread is shared.
 */
export function ThreadShareControl(props: ThreadShareControlProps) {
  const { threadToken, isShared, onThreadChange } = props

  const { t } = useTranslation('app')
  const [isSaving, setIsSaving] = useState(false)

  const onToggle = async (next: boolean) => {
    setIsSaving(true)
    try {
      const updated = await knowledgeThreadsApi.update(threadToken, { shared: next })
      onThreadChange(updated)
    } catch {
      toast.error(t('threads.detail.share.failed'))
    } finally {
      setIsSaving(false)
    }
  }

  const onCopyLink = async () => {
    const url = `${window.location.origin}${ROUTES.thread(threadToken)}`
    try {
      await navigator.clipboard.writeText(url)
      toast.success(t('threads.detail.share.linkCopied'))
    } catch {
      toast.error(t('threads.detail.share.copyFailed'))
    }
  }

  const label = isShared ? t('threads.detail.share.shared') : t('threads.detail.share.button')

  return (
    <Popover>
      <PopoverTrigger
        data-testid="thread-share-button"
        aria-label={label}
        className={cn(
          'inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-sm transition-colors',
          isShared
            ? 'text-foreground hover:bg-muted/60'
            : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
        )}
      >
        <Users className="h-4 w-4" strokeWidth={1.5} aria-hidden />
        <span className="hidden md:inline">{label}</span>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 space-y-3" data-testid="thread-share-popover">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">{t('threads.detail.share.title')}</p>
            <p className="text-xs text-muted-foreground">{t('threads.detail.share.hint')}</p>
          </div>
          <Switch
            checked={isShared}
            disabled={isSaving}
            onCheckedChange={(next) => void onToggle(next)}
            aria-label={t('threads.detail.share.toggle')}
            data-testid="thread-share-toggle"
          />
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full"
          // A private thread's link opens for nobody else, so there is nothing to copy yet.
          disabled={!isShared}
          onClick={() => void onCopyLink()}
          data-testid="thread-share-copy-link"
        >
          <Link2 className="mr-1.5 h-4 w-4" aria-hidden />
          {t('threads.detail.share.copyLink')}
        </Button>
      </PopoverContent>
    </Popover>
  )
}
